import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'wasp/client/auth';
import { useQuery } from 'wasp/client/operations';
import {
  getNotificationPreferences,
  getNotifications,
  getUnreadNotificationCount,
} from 'wasp/client/operations';
import { Bell } from 'lucide-react';
import { cn } from '../../lib/utils';

const POLL_INTERVAL_MS = 15_000;
const POPUP_DURATION_MS = 4_000;

type NotificationPreferencesResponse = {
  global: {
    inAppPopupEnabled: boolean;
  };
};

/**
 * Polls for new in-app notifications and shows a brief top-right popup
 * when the unread count increases (if the user has pop-ups enabled).
 */
export default function NotificationInAppAlertManager() {
  const { data: user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [popupTitle, setPopupTitle] = useState('Notification');
  const prevCountRef = useRef<number | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: rawPrefs } = useQuery(getNotificationPreferences, {}, { enabled: !!user });
  const prefs = rawPrefs as NotificationPreferencesResponse | undefined;
  const popupEnabled = prefs?.global?.inAppPopupEnabled ?? true;

  const { data: unreadData } = useQuery(
    getUnreadNotificationCount,
    {},
    { enabled: !!user, refetchInterval: POLL_INTERVAL_MS }
  );

  useEffect(() => {
    if (!user || !popupEnabled) {
      prevCountRef.current = unreadData?.count ?? null;
      return;
    }

    const count = unreadData?.count ?? 0;

    if (prevCountRef.current !== null && count > prevCountRef.current) {
      void (async () => {
        try {
          const latest = await getNotifications({ limit: 1, unreadOnly: true });
          const newest = latest?.[0];
          setPopupTitle(newest?.title?.trim() || 'Notification');
        } catch {
          setPopupTitle('Notification');
        }
        setVisible(true);

        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = setTimeout(() => {
          setVisible(false);
        }, POPUP_DURATION_MS);
      })();
    }

    prevCountRef.current = count;
  }, [unreadData?.count, user, popupEnabled]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed top-4 right-4 z-[200] flex max-w-sm items-start gap-3 rounded-lg border border-border',
        'bg-background px-4 py-3 shadow-lg animate-in slide-in-from-top-2 fade-in duration-300'
      )}
    >
      <Bell className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{popupTitle}</p>
        <p className="text-xs text-muted-foreground">You have a new notification</p>
      </div>
    </div>
  );
}
